import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@/lib/toast";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { STAGES, fmtDate, origemLabel, roleLabel, slaInfo, SLA_HOURS, prioLabel, slackLink, slackThread } from "@/lib/constants";
import { Page, StageBadge, PrioBadge, Empty, readFileAsDataUrl, SlaBadge, useNow, slaBg, Timeline, Modal } from "@/components/ui-z";
import { useTicketActions, useDeleteTicket } from "@/components/TicketActions";
import MentionTextarea from "@/components/MentionTextarea";
import RedmineModal from "@/components/RedmineModal";
import { api } from "@/lib/api";

// Evidências novas ficam em arquivo no servidor e a rota exige o token, então vêm como blob (não dá para usar a URL
// direto no <img>). As antigas ainda trazem o data_url. Imagens carregam na hora; os outros arquivos só ao clicar
const evidenceBlob = async (ticketId, evidenceId) =>
  URL.createObjectURL((await api.get(`/tickets/${ticketId}/evidences/${evidenceId}/file`, { responseType: "blob" })).data);

const EvidenceCard = ({ ticketId, ev, onZoom }) => {
  const isImage = ev.content_type.startsWith("image/");
  const [src, setSrc] = useState(ev.data_url || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (ev.data_url || !isImage) return undefined;
    let url = null;
    let alive = true;
    evidenceBlob(ticketId, ev.id).then((u) => { url = u; if (alive) setSrc(u); else URL.revokeObjectURL(u); }).catch(() => alive && setFailed(true));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [ticketId, ev.id, ev.data_url, isImage]);

  const download = async (e) => {
    if (ev.data_url) return; // o link já tem o conteúdo
    e.preventDefault();
    try {
      const url = await evidenceBlob(ticketId, ev.id);
      const a = Object.assign(document.createElement("a"), { href: url, download: ev.name });
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Não foi possível baixar o arquivo"); }
  };

  return (
    <div className="col-sm-3 col-6 mb-3" data-testid={`evidence-${ev.id}`}>
      {isImage && src ? (
        <img src={src} alt={ev.name} className="img-fluid img-thumbnail mb-1" style={{ cursor: "zoom-in" }} onClick={() => onZoom({ name: ev.name, src })} />
      ) : (
        <div className="img-thumbnail text-center py-4 mb-1">
          <i className={`fas ${isImage && !failed ? "fa-spinner fa-spin" : failed ? "fa-exclamation-triangle text-warning" : "fa-file"} fa-2x text-muted`} />
        </div>
      )}
      <a href={ev.data_url || "#"} download={ev.name} onClick={download} className="d-block text-sm text-truncate">{ev.name}</a>
    </div>
  );
};

// Anexo dentro da bolha do chat: imagem em miniatura (clique amplia) ou o nome do arquivo para baixar.
// Usa o mesmo carregamento autenticado das evidências (blob)
const ChatAttachment = ({ ticketId, ev, onZoom }) => {
  const isImage = ev.content_type.startsWith("image/");
  const [src, setSrc] = useState(ev.data_url || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (ev.data_url || !isImage) return undefined;
    let url = null;
    let alive = true;
    evidenceBlob(ticketId, ev.id).then((u) => { url = u; if (alive) setSrc(u); else URL.revokeObjectURL(u); }).catch(() => alive && setFailed(true));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [ticketId, ev.id, ev.data_url, isImage]);

  const download = async (e) => {
    e.preventDefault();
    try {
      const url = ev.data_url || await evidenceBlob(ticketId, ev.id);
      Object.assign(document.createElement("a"), { href: url, download: ev.name }).click();
      if (!ev.data_url) setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Não foi possível baixar o arquivo"); }
  };

  if (isImage && src) {
    return <img src={src} alt={ev.name} title={ev.name} data-testid={`chat-attachment-${ev.id}`} className="rounded bg-white"
      style={{ maxWidth: 260, maxHeight: 200, objectFit: "contain", cursor: "zoom-in" }} onClick={() => onZoom({ name: ev.name, src })} />;
  }
  if (isImage && !failed) return <span className="text-sm"><i className="fas fa-spinner fa-spin mr-1" />{ev.name}</span>;
  return (
    <a href="#download" onClick={download} data-testid={`chat-attachment-${ev.id}`} className="badge badge-light text-sm font-weight-normal p-2">
      <i className={`fas ${failed ? "fa-exclamation-triangle text-warning" : "fa-paperclip"} mr-1`} />{ev.name}
    </a>
  );
};

const SlaCard = ({ t }) => {
  const now = useNow(15000);
  const s = slaInfo(t, now);
  return (
    <div className="card card-outline card-primary" data-testid="ticket-sla-card">
      <div className="card-header"><h3 className="card-title"><i className="far fa-clock mr-1" />Prazo (SLA)</h3></div>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <SlaBadge ticket={t} now={now} testId="ticket-sla-badge" />
          <span className="text-muted text-sm">{SLA_HOURS[t.prioridade]}h &middot; {prioLabel(t.prioridade)}</span>
        </div>
        <div className="progress progress-sm">
          <div data-testid="ticket-sla-progress" className={`progress-bar ${slaBg(s.state)}`} role="progressbar" style={{ width: `${s.pct}%` }} aria-valuenow={Math.round(s.pct)} aria-valuemin="0" aria-valuemax="100" />
        </div>
        <small className="text-muted">Vence em <b data-testid="ticket-sla-deadline">{fmtDate(s.deadline)}</b></small>
      </div>
    </div>
  );
};

// Checks no estilo do WhatsApp nas mensagens do portal para a conversa do Slack:
//   ✓ registrada no chamado · ✓✓ cinza entregue no Slack · ✓✓ azul alguém respondeu no Slack depois dela
//   ⚠ não entregue no Slack (URL de retorno, n8n ou bot fora do canal)
// O Slack não informa leitura de mensagem, então o azul é "teve resposta", não "foi lida"
const DeliveryChecks = ({ c, respondido }) => {
  if (c.actor_role === "slack" || c.interno) return null;
  const [icone, cor, titulo] =
    c.entrega === "falhou" ? ["fas fa-exclamation-triangle", "text-danger", "Não foi entregue no Slack"]
    : c.entrega === "enviado" && respondido ? ["fas fa-check-double", "text-primary", "Entregue no Slack e já respondida"]
    : c.entrega === "enviado" ? ["fas fa-check-double", "text-muted", "Entregue no Slack"]
    : ["fas fa-check", "text-muted", "Registrada no chamado"];
  return <i className={`${icone} ${cor} ml-2 text-xs`} title={titulo} data-testid={`comment-delivery-${c.id}`} aria-label={titulo} />;
};

// Avatar do direct chat (AdminLTE usa <img class="direct-chat-img">): iniciais com cor fixa por nome; quem respondeu
// pelo Slack ganha o selo do Slack
// Atualização periódica da página do chamado: 5 s sem o tempo real (SignalR); com ele, só uma conferência de reserva
const CHAT_POLL_MS = 5000;
// Arquivos por mensagem da Comunicação (o mesmo limite da API)
const MAX_ANEXOS = 5;
const MAX_ANEXO_MB = 10;
const CHAT_POLL_REALTIME_MS = 60000;

const AVATAR_CORES =["#007bff", "#6f42c1", "#17a2b8", "#28a745", "#fd7e14", "#e83e8c", "#20c997", "#6610f2"];
const ChatAvatar = ({ name, slack }) => {
  const nome = (name || "?").trim();
  const ini = nome.split(/\s+/).filter(Boolean).map((w) => w[0]).filter((_, i, a) => i === 0 || i === a.length - 1).join("").toUpperCase();
  const cor = AVATAR_CORES[[...nome].reduce((h, ch) => h + ch.charCodeAt(0), 0) % AVATAR_CORES.length];
  return (
    <span className="direct-chat-img d-inline-flex align-items-center justify-content-center text-white font-weight-bold position-relative"
      style={{ background: cor, fontSize: 14 }} aria-hidden="true">
      {ini}
      {slack && <i className="fab fa-slack position-absolute bg-white rounded-circle text-dark" style={{ right: -4, bottom: -4, fontSize: 12, padding: 2 }} />}
    </span>
  );
};

export default function ChamadoDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { details, loadTicket, reopenTicket, addComment, addEvidence, realtime } = useStore();
  const actions = useTicketActions();
  const del = useDeleteTicket();
  const nav = useNavigate();
  const t = details[id];
  const [tab, setTab] = useState("comentarios");
  const [msg, setMsg] = useState("");
  const [interno, setInterno] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [redmine, setRedmine] = useState(false);
  const msgRef = useRef(null);
  // Conversa do chamado no Slack (aberto pelo /novochamado): mensagens públicas vão também para a thread
  const thread = slackThread(t?.origem_ref);
  // Envolvidos na conversa, para a menção com "@": solicitante, quem respondeu pelo Slack e o time que já escreveu.
  // "slack": recebe a menção de verdade no Slack (a API troca "@Nome" por <@U...>)
  const pessoas = useMemo(() => {
    const mapa = new Map();
    // externo: solicitante ou quem respondeu pelo Slack. Só a SUA entrada como atendente do portal fica de fora;
    // se você também é o solicitante no Slack (mesmo nome), continua na lista
    const add = (nome, papel, slack, externo) => {
      const n = (nome || "").trim();
      if (!n || (!externo && n === user?.name)) return;
      const k = n.toLowerCase();
      const atual = mapa.get(k);
      mapa.set(k, { nome: n, papel: atual?.papel || papel, slack: !!(atual?.slack || slack) });
    };
    if (t) {
      add(t.solicitante_nome, "Solicitante", !!thread?.user, true);
      for (const c of t.comentarios || []) {
        const doSlack = c.actor_role === "slack";
        add(c.actor_name, doSlack ? "Slack" : roleLabel(c.actor_role), doSlack && !!c.autor_ref, doSlack);
      }
    }
    return [...mapa.values()];
  }, [t, thread?.user, user?.name]);


  // Arquivos da mensagem (colados com Ctrl+V, arrastados ou pelo botão Anexar). Vão juntos para o Slack
  const [anexos, setAnexos] = useState([]);
  const [arrastando, setArrastando] = useState(false);
  const adicionarArquivos = (lista) => {
    const novos = [];
    for (const f of lista) {
      if (f.size > MAX_ANEXO_MB * 1024 * 1024) { toast.warning(`"${f.name}" tem mais de ${MAX_ANEXO_MB} MB.`); continue; }
      // Print colado chega como "image.png": nome com data e hora para não repetir
      const nome = f.name === "image.png" ? `print-${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "")}.png` : f.name;
      const file = nome === f.name ? f : new File([f], nome, { type: f.type });
      novos.push({ key: `${Date.now()}-${Math.random()}`, file, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null });
    }
    setAnexos((atual) => {
      const todos = [...atual, ...novos];
      if (todos.length > MAX_ANEXOS) toast.warning(`No máximo ${MAX_ANEXOS} arquivos por mensagem.`);
      todos.slice(MAX_ANEXOS).forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
      return todos.slice(0, MAX_ANEXOS);
    });
  };
  const removerAnexo = (key) => setAnexos((atual) => {
    atual.filter((a) => a.key === key).forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
    return atual.filter((a) => a.key !== key);
  });

  const enviar = async () => {
    if (!msg.trim() && anexos.length === 0) return;
    let evidencias = [];
    try { evidencias = await Promise.all(anexos.map((a) => readFileAsDataUrl(a.file))); }
    catch { return toast.error("Não foi possível ler um dos arquivos."); }
    const res = await addComment(t.id, msg, interno, evidencias);
    if (!res) return;
    setMsg("");
    anexos.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
    setAnexos([]);
    if (res.slack === "falhou") toast.warning("Mensagem registrada no chamado, mas não foi entregue no Slack. Verifique a URL de retorno da integração.");
    else toast.success(res.slack === "enviado" ? "Mensagem registrada e enviada no Slack" : "Mensagem registrada");
  };

  useEffect(() => { loadTicket(id); }, [id, loadTicket]);

  // Com o tempo real (SignalR) a conversa atualiza sozinha quando chega mensagem. Sem ele (conexão caiu ou proxy sem
  // WebSocket), consulta a cada 5 s. Só com a aba do navegador visível, e na hora em que o usuário volta para ela
  useEffect(() => {
    const atualizar = () => { if (document.visibilityState === "visible") loadTicket(id, { silent: true }); };
    const timer = setInterval(atualizar, realtime ? CHAT_POLL_REALTIME_MS : CHAT_POLL_MS);
    document.addEventListener("visibilitychange", atualizar);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", atualizar); };
  }, [id, loadTicket, realtime]);

  // Chat sempre mostrando a última mensagem (como no WhatsApp) ao abrir a aba e quando chega mensagem nova
  const chatRef = useRef(null);
  const qtdMensagens = t?.comentarios?.length ?? 0;
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [qtdMensagens, tab]);

  if (t === undefined) return (
    <Page title="Chamado" crumbs={[["Chamados", "/chamados"], ["..."]]}>
      <div className="card" data-testid="ticket-loading" style={{ minHeight: 200 }}>
        <div className="card-body" />
        <div className="overlay"><i className="fas fa-2x fa-sync-alt fa-spin" /></div>
      </div>
    </Page>
  );
  if (!t) return <Page title="Chamado" crumbs={[["Chamados", "/chamados"], ["?"]]}><Empty testId="ticket-not-found" text="Chamado não foi encontrado. Ele pode ter sido excluído ou o link está incorreto." /></Page>;
  const resolved = t.status === "resolvido";
  const cur = STAGES.findIndex((x) => x.key === t.stage);

  const TABS = [
    { k: "comentarios", l: `Comunicação (${t.comentarios.length})`, i: "fas fa-comments" },
    { k: "evidencias", l: `Evidências (${t.evidencias.length})`, i: "fas fa-paperclip" },
    { k: "historico", l: "Histórico", i: "fas fa-history" },
  ];

  return (
    <Page title={`${t.codigo} · ${t.titulo}`} crumbs={[["Chamados", "/chamados"], [t.codigo]]}>
      <div className="progress mb-3" style={{ height: 24 }} data-testid="stage-progress">
        {STAGES.map((s, i) => (
          <div key={s.key} className={`progress-bar ${i <= cur ? "bg-primary" : "bg-light text-muted"}`} style={{ width: `${100 / STAGES.length}%`, borderRight: "1px solid rgba(0,0,0,.1)" }}>
            {s.short}
          </div>
        ))}
      </div>

      <div className="row">
        <div className="col-lg-8">
          <div className="card card-primary card-outline">
            <div className="card-header">
              <h3 className="card-title">Descrição</h3>
              <div className="card-tools">
                <StageBadge stage={t.stage} testId="ticket-stage-badge" /> <PrioBadge prio={t.prioridade} testId="ticket-priority-badge" />
              </div>
            </div>
            <div className="card-body">
              <p className="mb-0" style={{ whiteSpace: "pre-wrap" }} data-testid="ticket-description">{t.descricao}</p>
              {t.solucao && <div className="callout callout-success mt-3 mb-0" data-testid="ticket-solution"><h5>Solução</h5><p>{t.solucao}</p></div>}
            </div>
          </div>

          <div className="card card-primary card-outline card-outline-tabs">
            <div className="card-header p-0 border-bottom-0">
              <ul className="nav nav-tabs" role="tablist">
                {TABS.map((x) => (
                  <li key={x.k} className="nav-item">
                    <a href="#!" role="tab" data-testid={`tab-${x.k}`} className={`nav-link ${tab === x.k ? "active" : ""}`} onClick={(e) => { e.preventDefault(); setTab(x.k); }}>
                      <i className={`${x.i} mr-1`} />{x.l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-body">
              {tab === "comentarios" && (
                <>
                  {/* Direct chat do AdminLTE: time de atendimento à direita (bolha azul), solicitante/Slack à esquerda (cinza) */}
                  <div className="direct-chat direct-chat-primary mb-3">
                    <div ref={chatRef} className="direct-chat-messages" data-testid="comments-list" style={{ height: "auto", maxHeight: 460 }}>
                      {t.comentarios.length === 0 && <Empty text="Nenhuma mensagem." />}
                      {t.comentarios.map((c, i) => {
                        const doTime = c.actor_role !== "slack";
                        return (
                          <div key={c.id} className={`direct-chat-msg ${doTime ? "right" : ""}`} data-testid={`comment-${c.id}`}>
                            <div className="direct-chat-infos clearfix">
                              <span className={`direct-chat-name ${doTime ? "float-right" : "float-left"}`}>
                                {c.actor_name} <small className="text-muted font-weight-normal">({roleLabel(c.actor_role)})</small>
                                {c.interno && <span className="badge badge-warning ml-1"><i className="fas fa-lock mr-1" />Nota interna</span>}
                              </span>
                              <span className={`direct-chat-timestamp ${doTime ? "float-left" : "float-right"}`}>
                                {fmtDate(c.timestamp)}
                                <DeliveryChecks c={c} respondido={t.comentarios.slice(i + 1).some((x) => x.actor_role === "slack")} />
                              </span>
                            </div>
                            <ChatAvatar name={c.actor_name} slack={!doTime} />
                            {/* Nota interna em amarelo para não ser confundida com mensagem enviada ao solicitante */}
                            <div className={`direct-chat-text ${c.interno ? "bg-warning border-warning" : ""}`} style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                              {c.mensagem}
                              {/* Imagens/arquivos enviados junto com a mensagem (ex.: print colado na thread do Slack) */}
                              {t.evidencias.some((ev) => ev.comment_id === c.id) && (
                                <div className={`d-flex flex-wrap ${c.mensagem ? "mt-2" : ""}`} style={{ gap: 6, whiteSpace: "normal" }}>
                                  {t.evidencias.filter((ev) => ev.comment_id === c.id).map((ev) => (
                                    <ChatAttachment key={ev.id} ticketId={t.id} ev={ev} onZoom={setZoom} />
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div
                    className={`form-group rounded ${arrastando ? "border border-primary" : ""}`}
                    onDragOver={(e) => { if ([...e.dataTransfer.types].includes("Files")) { e.preventDefault(); setArrastando(true); } }}
                    onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setArrastando(false); }}
                    onDrop={(e) => { if (e.dataTransfer.files.length) { e.preventDefault(); setArrastando(false); adicionarArquivos(e.dataTransfer.files); } }}
                  >
                    <MentionTextarea ref={msgRef} data-testid="comment-input" rows={3} className="form-control" pessoas={pessoas}
                      placeholder={pessoas.length ? "Escreva uma mensagem... (use @ para mencionar, cole ou arraste arquivos)" : "Escreva uma mensagem... (cole ou arraste arquivos)"}
                      value={msg} onChange={setMsg}
                      onPaste={(e) => { if (e.clipboardData?.files?.length) { e.preventDefault(); adicionarArquivos(e.clipboardData.files); } }} />
                    {anexos.length > 0 && (
                      <div className="d-flex flex-wrap mt-2" data-testid="comment-attachments">
                        {anexos.map((a) => (
                          <div key={a.key} className="d-flex align-items-center border rounded mr-2 mb-1 pr-1 bg-light" style={{ maxWidth: 240 }}>
                            {a.preview
                              ? <img src={a.preview} alt="" style={{ width: 40, height: 40, objectFit: "cover" }} className="rounded-left" />
                              : <i className="fas fa-file fa-lg text-muted mx-2" />}
                            <small className="text-truncate mx-2" title={a.file.name}>{a.file.name}</small>
                            <button type="button" className="btn btn-tool p-0" title="Remover" onClick={() => removerAnexo(a.key)}><i className="fas fa-times" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <small className={interno || !thread ? "text-muted" : "text-info"} data-testid="comment-slack-hint">
                        {thread && <i className="fab fa-slack mr-1" />}
                        {!thread ? "" : interno ? "Nota interna: não vai para o Slack." : "Também vai para a conversa do chamado no Slack, com o seu nome e cargo (e os arquivos)."}
                      </small>
                      <label className="btn btn-xs btn-default mb-0" title={`Anexar arquivos (até ${MAX_ANEXOS}, ${MAX_ANEXO_MB} MB cada)`}>
                        <i className="fas fa-paperclip mr-1" />Anexar
                        <input type="file" multiple hidden data-testid="comment-attach-input" onChange={(e) => { adicionarArquivos(e.target.files); e.target.value = ""; }} />
                      </label>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="custom-control custom-checkbox">
                      <input id="comment-internal" data-testid="comment-internal-checkbox" type="checkbox" className="custom-control-input" checked={interno} onChange={(e) => setInterno(e.target.checked)} />
                      <label htmlFor="comment-internal" className="custom-control-label">Nota interna</label>
                    </div>
                    <div>
                      {!resolved && (
                        <>
                          {/* T3 validou que precisa de desenvolvimento: vira tarefa no Redmine */}
                          {t.stage === "t3_responsavel_tecnico" && (
                            <button type="button" data-testid="open-redmine-btn" className="btn btn-danger mr-2" onClick={() => setRedmine(true)}>
                              <i className="fas fa-share-square mr-1" />Enviar para Redmine
                            </button>
                          )}
                          <button type="button" data-testid="open-transfer-btn" className="btn btn-warning mr-2" onClick={() => actions.showTransfer(t)}>
                            <i className="fas fa-exchange-alt mr-1" />Transferir
                          </button>
                          <button type="button" data-testid="open-resolve-btn" className="btn btn-success mr-2" onClick={() => actions.showResolve(t)}>
                            <i className="fas fa-check-circle mr-1" />Resolver
                          </button>
                        </>
                      )}
                      <button type="button" data-testid="comment-submit-btn" className="btn btn-primary" disabled={!msg.trim() && anexos.length === 0} onClick={enviar}>
                        <i className="fas fa-paper-plane mr-1" />Enviar
                      </button>
                    </div>
                  </div>
                </>
              )}
              {tab === "evidencias" && (
                <>
                  <div className="row" data-testid="evidence-grid">
                    {t.evidencias.map((ev) => <EvidenceCard key={ev.id} ticketId={t.id} ev={ev} onZoom={setZoom} />)}
                  </div>
                  {t.evidencias.length === 0 && <Empty text="Nenhuma evidência anexada." />}
                  <div className="custom-file">
                    <input id="add-evidence" data-testid="add-evidence-input" type="file" multiple className="custom-file-input" onChange={async (e) => {
                      let ok = true;
                      for (const f of e.target.files) ok = !!(await addEvidence(t.id, await readFileAsDataUrl(f), user)) && ok;
                      e.target.value = "";
                      if (ok) toast.success("Evidência anexada");
                    }} />
                    <label className="custom-file-label" htmlFor="add-evidence">Anexar evidência</label>
                  </div>
                </>
              )}
              {tab === "historico" && (t.historico.length ? <Timeline items={t.historico} /> : <Empty text="Sem registros no histórico." />)}
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <SlaCard t={t} />

          <div className="card card-outline card-primary" data-testid="ticket-info">
            <div className="card-header">
              <h3 className="card-title"><i className="fas fa-info-circle mr-1" />Informações</h3>
              <div className="card-tools">
                <button type="button" className="btn btn-tool" title="Ações" data-testid="ticket-actions-btn" onClick={(e) => actions.open(t, e)}><i className="fas fa-ellipsis-v" /></button>
              </div>
            </div>
            <div className="card-body">
              <dl className="row mb-0">
                {[
                  ["Solicitante", t.solicitante_nome], ["Contato", t.solicitante_contato || "—"],
                  ["Responsável", t.assigned_to_name || "Não atribuído"], [t.produto_id ? "Produto" : "Categoria", t.categoria || "—"],
                  ["Aberto por", t.created_by_name], ["Aberto em", fmtDate(t.created_at)],
                  ["Atualizado", fmtDate(t.updated_at)], ["Resolvido em", fmtDate(t.resolved_at)],
                ].map(([k, v]) => (
                  <div key={k} className="col-12 d-flex justify-content-between">
                    <dt className="font-weight-normal text-muted">{k}</dt><dd className="text-right mb-1">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          <div className="card card-outline card-secondary" data-testid="ticket-origin">
            <div className="card-header"><h3 className="card-title"><i className="fas fa-link mr-1" />Conversa de origem</h3></div>
            <div className="card-body">
              <strong>{origemLabel(t.origem)}</strong>
              {/* Referência do Slack (slack:T…:C…:ts:U…) é interna: o botão abaixo já leva à conversa. Outras origens mostram o id */}
              {!slackLink(t.origem_ref) && <p className="text-muted mb-1"><code>{t.origem_ref || "sem vínculo"}</code></p>}
              {slackLink(t.origem_ref) && (
                <a href={slackLink(t.origem_ref)} data-testid="origin-slack-link" className="btn btn-default btn-sm btn-block mb-2">
                  <i className="fab fa-slack mr-1" />Abrir conversa no Slack
                </a>
              )}
              <small className="text-muted">GET /api/tickets/{t.id}</small>
            </div>
          </div>

          {resolved && (
            <button type="button" data-testid="reopen-btn" className="btn btn-default btn-block mb-3" onClick={async () => { if (await reopenTicket(t.id, user)) toast("Chamado reaberto em T1"); }}>
              <i className="fas fa-redo mr-1" />Reabrir chamado
            </button>
          )}
          {del.allowed && (
            <button type="button" data-testid="delete-ticket-btn" className="btn btn-outline-danger btn-block mb-3"
              onClick={async () => { if (await del.remove(t)) nav("/chamados", { replace: true }); }}>
              <i className="fas fa-trash mr-1" />Excluir chamado
            </button>
          )}
        </div>
      </div>

      {zoom && (
        <Modal testId="evidence-zoom-modal" size="modal-xl" title={zoom.name} onClose={() => setZoom(null)}>
          <img src={zoom.src} alt={zoom.name} className="img-fluid d-block mx-auto" />
        </Modal>
      )}
      {redmine && <RedmineModal t={t} onClose={() => setRedmine(false)} />}
      {actions.element}
    </Page>
  );
}
